/**
 * Shared options and helpers for supplemental mapping (composition) and template default pipeline UI.
 */

export const DEFAULT_BADGE_LABEL = 'item'

export const SUPPLEMENTAL_SOURCE_TYPE_OPTIONS = [
  { label: 'Pipeline', value: 'pipeline' },
  { label: 'Smart Endpoint', value: 'smartEndpoint' }
]

export const SUPPLEMENTAL_LAYOUT_OPTIONS = [
  { label: 'Tasks Accordion', value: 'tasksAccordion' },
  { label: 'Tasks List', value: 'tasksList' },
  { label: 'Smart Component', value: 'smartComponent' }
]

export function includeCurrentSelectValue (options, currentValue) {
  if (!currentValue || options.some((option) => option.value === currentValue)) return options
  return [...options, { label: currentValue, value: currentValue }]
}

export const SUPPLEMENTAL_MATCH_OP_OPTIONS = [
  { label: 'Equals', value: 'eq' },
  { label: 'Not equals', value: 'neq' },
  { label: 'In', value: 'in' },
  { label: 'Not in', value: 'nin' }
]

export function formatMatchValueForForm (value) {
  if (value == null) return ''
  if (Array.isArray(value) || (typeof value === 'object' && value !== null && value.constructor === Object)) {
    return JSON.stringify(value)
  }
  return String(value)
}

/**
 * @param {string} valueFieldPath - react-hook-form field for setError (e.g. matchConfig.value)
 */
export function parseMatchValueForSubmit (raw, op, form, valueFieldPath = 'matchConfig.value') {
  const t = typeof raw === 'string' ? raw.trim() : ''
  if (op === 'in' || op === 'nin') {
    if (!t) {
      form.setError(valueFieldPath, { type: 'validate', message: 'Required' })
      return undefined
    }
    if (t.startsWith('[')) {
      try {
        const arr = JSON.parse(t)
        if (!Array.isArray(arr)) throw new Error('expected array')
        return arr
      } catch {
        form.setError(valueFieldPath, { type: 'validate', message: 'Enter a JSON array' })
        return undefined
      }
    }
    return t.split(',').map(s => s.trim()).filter(Boolean)
  }
  if (!t) {
    form.setError(valueFieldPath, { type: 'validate', message: 'Required' })
    return undefined
  }
  return t
}

export const SUPPLEMENTAL_FIELD_MAP_KEYS = [
  { key: 'label', label: 'Label / title', placeholder: 'e.g. Subject', defaultField: 'taskName' },
  { key: 'status', label: 'Status', placeholder: 'e.g. completedStatus', defaultField: 'completedStatus' },
  { key: 'type', label: 'Type badge', placeholder: 'e.g. taskType', defaultField: 'taskType' }
]

export const DETAIL_FIELD_FORMAT_OPTIONS = [
  { label: 'None (raw value)', value: '' },
  { label: 'Date', value: 'date' },
  { label: 'Date + time', value: 'timestamp' },
  { label: 'Number', value: 'number' },
  { label: 'Currency', value: 'currency' },
  { label: 'Percentage', value: 'percentage' },
  { label: 'Boolean (Y/N)', value: 'bool' }
]

const MANAGED_DISPLAY_CONFIG_KEYS = new Set([
  'layout',
  'itemsPath',
  'header',
  'summaryLabel',
  'detailFields',
  'completedValue',
  'fields',
  'blueprintCode',
  'presentationKey',
  'idField',
  'responseItemsPath',
  'containerStyle'
])

function getPreservedDisplayConfigFields (baseConfig) {
  if (!baseConfig || typeof baseConfig !== 'object' || Array.isArray(baseConfig)) return {}
  const preserved = {}
  for (const [key, value] of Object.entries(baseConfig)) {
    if (!MANAGED_DISPLAY_CONFIG_KEYS.has(key)) preserved[key] = value
  }
  return preserved
}

/** Convert detailFieldRows from form state into the clean array persisted in displayConfig. */
export function buildDetailFieldsFromRows (rows) {
  if (!Array.isArray(rows)) return undefined
  const cleaned = rows.reduce((acc, r) => {
    const field = r.field?.trim()
    if (!field) return acc
    const entry = { field }
    const label = r.label?.trim()
    if (label) entry.label = label
    const format = r.format?.trim()
    if (format) entry.format = format
    acc.push(entry)
    return acc
  }, [])
  return cleaned.length ? cleaned : undefined
}

/** Convert persisted detailFields array back into form-friendly rows. */
export function detailFieldsToRows (detailFields) {
  if (!Array.isArray(detailFields) || !detailFields.length) return []
  return detailFields.map(df => ({
    field: df.field ?? '',
    label: df.label ?? '',
    format: df.format ?? ''
  }))
}

/**
 * @param {object} d - { layout, itemsPath?, header?, detailFieldRows?, fields? }
 * @param {object} [options]
 * @param {boolean} [options.includeItemsPath]
 * @param {object} [options.baseConfig]
 */
export function buildSupplementalDisplayConfigPayload (d, options = {}) {
  const { includeItemsPath = false, baseConfig } = options
  const layout = d.layout?.trim()
  if (!layout) return null
  const detailFields = buildDetailFieldsFromRows(d.detailFieldRows)
  const out = getPreservedDisplayConfigFields(baseConfig)
  out.layout = layout
  if (includeItemsPath) {
    const ip = d.itemsPath?.trim()
    if (ip) out.itemsPath = ip
  }
  const header = d.header?.trim()
  if (header) out.header = header
  const sl = d.summaryLabel?.trim()
  if (sl) out.summaryLabel = sl
  if (detailFields !== undefined) out.detailFields = detailFields

  const cv = d.completedValue?.trim()
  if (cv) out.completedValue = cv

  if (d.fields) {
    const fields = {}
    for (const { key, defaultField } of SUPPLEMENTAL_FIELD_MAP_KEYS) {
      const v = d.fields[key]?.trim()
      if (v && v !== defaultField) fields[key] = v
    }
    if (Object.keys(fields).length) out.fields = fields
  }

  if (layout === 'smartComponent') {
    const bp = d.blueprintCode?.trim()
    if (bp) out.blueprintCode = bp
    const pk = d.presentationKey?.trim()
    if (pk) out.presentationKey = pk
    const idf = d.idField?.trim()
    if (idf) out.idField = idf
    const rip = d.responseItemsPath?.trim()
    if (rip) out.responseItemsPath = rip
    const rawContainerStyle = d.containerStyle?.trim()
    if (rawContainerStyle) {
      try {
        out.containerStyle = JSON.parse(rawContainerStyle)
      } catch {
        if (baseConfig?.containerStyle !== undefined) {
          out.containerStyle = baseConfig.containerStyle
        }
      }
    }
  }

  return Object.keys(out).length ? out : null
}

/**
 * Merges optional board-row badge settings into `defaultDisplayConfig` (template definition).
 * When `badgeForm.show` is false, `badge` is removed from the saved config.
 */
export function applySupplementalBadgeToDisplayConfig (displayConfig, badgeForm) {
  if (!displayConfig || !badgeForm) return displayConfig
  if (badgeForm.show === true) {
    const badge = { show: true }
    const icon = typeof badgeForm.icon === 'string' ? badgeForm.icon.trim() : ''
    if (icon) badge.icon = icon
    const raw = typeof badgeForm.style === 'string' ? badgeForm.style.trim() : ''
    if (raw) {
      try { badge.style = JSON.parse(raw) } catch { /* invalid JSON — skip */ }
    }
    const label = typeof badgeForm.label === 'string' ? badgeForm.label.trim() : ''
    if (label && label !== DEFAULT_BADGE_LABEL) badge.label = label
    return { ...displayConfig, badge }
  }
  const next = { ...displayConfig }
  delete next.badge
  return next
}
